import { Button } from "@/components/ui/button";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";
import { useProjectSchedulesContext } from "@/modules/projects/components/schedules/ProjectSchedulesContext";
import { CornerLeftUpIcon, PlusIcon } from "lucide-react";
import { memo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { ProjectSchedulesDeleteSelectedButton } from "./ProjectSchedulesDeleteSelectedButton";
import { useCheckIsProjectLocked } from "../../hooks/useCheckIsProjectLocked";
import { useRowSelectionCount } from "@/components/data-table/hooks/useRowSelectionCount";

const ProjectSchedulesTableFooterComponent = () => {
  const { t } = useTranslation();
  const { can_update, can_delete } = useModulePermissions();
  const isProjectLocked = useCheckIsProjectLocked();
  const {
    setIsUpdateDialogOpened: setOpenUpdateDialog,
    setIsAddDialogOpened,
    setIsDeleteDialogOpened,
  } = useProjectSchedulesContext();

  const onUpdateSchedulesClick = useCallback(() => {
    setOpenUpdateDialog(true);
  }, [setOpenUpdateDialog]);

  const onAddSchedulesClick = useCallback(() => {
    setIsAddDialogOpened(true);
  }, [setIsAddDialogOpened]);

  const onDeleteSchedulesClick = useCallback(() => {
    setIsDeleteDialogOpened(true);
  }, [setIsDeleteDialogOpened]);

  const selectionCount = useRowSelectionCount();
  const isRowSelected = selectionCount > 0;

  const deleteSchedulesDisabled =
    !isRowSelected || isProjectLocked || !can_delete;
  const updateSchedulesDisabled =
    !isRowSelected || !can_update || isProjectLocked;

  // We are checking can_update instead of can_create, because this is an update (PATCH) in the API
  const addSchedulesDisabled = !can_update || isProjectLocked;

  return (
    <div className="bg-slate-200/70 sticky bottom-1 backdrop-blur-md">
      <div className="hover:bg-transparent sticky left-0 px-1 py-3 flex items-center">
        <CornerLeftUpIcon className="w-4 h-4 text-slate-400 mx-3" />
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={updateSchedulesDisabled}
            onClick={onUpdateSchedulesClick}
          >
            {t("projectSchedules.buttons.updateSelectedSchedules")}
          </Button>
          <ProjectSchedulesDeleteSelectedButton
            disabled={deleteSchedulesDisabled}
            onClick={onDeleteSchedulesClick}
          />
          <Button
            size="sm"
            variant="secondary"
            onClick={onAddSchedulesClick}
            disabled={addSchedulesDisabled}
          >
            <PlusIcon className="w-4 h-4" />
            {t("projectSchedules.buttons.addSchedules")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export const ProjectSchedulesTableFooter = memo(
  ProjectSchedulesTableFooterComponent,
);
